import React, { useEffect, useRef, useState } from "react";
import {
  addDoc,
  collection,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import toast from "react-hot-toast";
import { db } from "../../helper/firebaseClient";
import { api } from "../../utils/axios-instance";

export default function DeliverableChat({
  orgId,
  roomId,
  deliverableId,
  userType,
  userName,
  token,
  passcode,
}) {
  const clientToken = token ?? passcode;
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const messagesCollection = () =>
    collection(
      db,
      "organizations",
      String(orgId),
      "deliveryRooms",
      String(roomId),
      "deliverables",
      String(deliverableId),
      "messages"
    );

  // Real-time listener — every message written by either party (client POST
  // hitting the Django backend, or vendor's direct Firestore write below)
  // shows up here automatically, in creation order.
  useEffect(() => {
    console.log("INSIDE DELIVERABLE CHAT");
    if (
      !roomId ||
      !deliverableId ||
      roomId === "undefined" ||
      deliverableId === "undefined"
    )
      return;

    const colRef = collection(
      db,
      "organizations",
      String(orgId),
      "deliveryRooms",
      String(roomId),
      "deliverables",
      String(deliverableId),
      "messages"
    );

    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const parsedMsgs = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              ...data,
              timestampMillis: data.createdAt?.toMillis
                ? data.createdAt.toMillis()
                : Date.now(),
            };
          })
          .sort((a, b) => a.timestampMillis - b.timestampMillis);

        console.log("Fetched deliverable messages:", parsedMsgs);
        setMessages(parsedMsgs);
      },
      (error) => {
        console.error("Failed to listen for deliverable messages:", error);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, deliverableId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;

    setSending(true);
    try {
      if (userType === "client") {
        await api.post(
          `/vendors/public/rooms/${roomId}/deliverables/${deliverableId}/messages/?token=${clientToken}`,
          { text, sender_name: userName }
        );
      } else {
        // Vendor side is already Firebase-authenticated org-side — write
        // straight to Firestore rather than round-tripping the backend.
        await addDoc(messagesCollection(), {
          text,
          senderType: "vendor",
          senderName: userName || "Vendor",
          createdAt: serverTimestamp(),
        });
      }
      setInputText("");
    } catch (err) {
      console.error("Failed to send deliverable message:", err);
      toast.error(err?.response?.data?.error || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "4px 2px",
        }}
      >
        {messages.length === 0 ? (
          <p
            style={{
              color: "#9ca3af",
              fontSize: "13px",
              textAlign: "center",
              marginTop: "12px",
            }}
          >
            No messages yet. Start the conversation.
          </p>
        ) : (
          messages.map((msg) => {
            const isOwn = msg.senderType === userType;
            return (
              <div
                key={msg.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isOwn ? "flex-end" : "flex-start",
                }}
              >
                <span
                  style={{
                    fontSize: "11px",
                    color: "#9ca3af",
                    marginBottom: "2px",
                  }}
                >
                  {msg.senderName ||
                    (msg.senderType === "vendor" ? "Vendor" : "Client")}
                </span>
                <div
                  style={{
                    maxWidth: "85%",
                    padding: "8px 12px",
                    borderRadius: "12px",
                    fontSize: "13px",
                    lineHeight: 1.4,
                    wordBreak: "break-word",
                    background: isOwn ? "#5E5ADB" : "#f3f4f6",
                    color: isOwn ? "#fff" : "#111827",
                  }}
                >
                  {msg.text}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
        <textarea
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add comments or feedback for this deliverable..."
          rows={2}
          style={{
            flex: 1,
            resize: "none",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 10px",
            fontSize: "13px",
            fontFamily: "inherit",
          }}
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={sending || !inputText.trim()}
          style={{
            alignSelf: "flex-end",
            background: sending || !inputText.trim() ? "#e5e7eb" : "#5E5ADB",
            color: sending || !inputText.trim() ? "#9ca3af" : "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: sending || !inputText.trim() ? "not-allowed" : "pointer",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
